import { yupResolver } from '@hookform/resolvers/yup';
import { Button, LinkSize, Text, TextColor, TextSize } from '@vizit/atoms';
import { Form, FormTextField } from '@vizit/forms';
import { Heading, HeadingVariant, RouterLink } from '@vizit/molecules';
import { useForm } from 'react-hook-form';
import * as yup from 'yup';
import { type LoginError, LoginErrorAlert } from '../login-error-alert';

type LoginFormSchema = {
  email: string;
  password: string;
};

interface LoginFormProps {
  error?: LoginError | null;
  onSubmit: (data: LoginFormSchema) => Promise<void>;
}

function LoginForm(props: LoginFormProps) {
  const { error, onSubmit } = props;

  // Form and validation schema
  const formSchema = yup.object().shape({
    email: yup.string().email('Invalid email format. Please try again.').required('Please enter your email.'),
    password: yup.string().required('Please enter your password.'),
  });

  const { control, formState, handleSubmit } = useForm<LoginFormSchema>({
    resolver: yupResolver(formSchema),
    defaultValues: {
      email: '',
      password: '',
    },
    mode: 'all',
    delayError: 600,
  });

  return (
    <div className="flex-grow flex w-full flex-col justify-center items-center gap-16" data-testid="login-form">
      <div className="flex flex-col items-center gap-16">
        <div className="flex flex-col items-center justify-center gap-4 min-h-[70px]">
          {error ? (
            <LoginErrorAlert error={error} />
          ) : (
            <>
              <Heading className="data-[error]:animate-fade-out" variant={HeadingVariant.H5}>
                Log in to your account
              </Heading>
              <Text className="data-[error]:animate-fade-out" size={TextSize.Md} color={TextColor.Tertiary}>
                Welcome back! Please enter your details.
              </Text>
            </>
          )}
        </div>
      </div>
      <div className="flex w-full flex-col justify-center items-center gap-8">
        <Form className="w-full max-w-[380px] gap-4" onSubmit={handleSubmit(onSubmit)}>
          <FormTextField
            type="email"
            label="Email"
            control={control}
            name="email"
            inputProps={{ 'data-testid': 'login-email-input' }}
          />
          <FormTextField
            inputProps={{ className: 'pendo-ignore', 'data-testid': 'login-password-input' }}
            type="password"
            label="Password"
            control={control}
            name="password"
          />

          <Button
            aria-label="Sign in"
            type="submit"
            isLoading={formState.isSubmitting}
            data-testid="login-submit-button"
          >
            Sign in
          </Button>
        </Form>
        <RouterLink size={LinkSize.Sm} to="/forgot-password" data-testid="login-forgot-password-link">
          Forgot your password?
        </RouterLink>
      </div>
    </div>
  );
}

export { LoginForm };
export type { LoginFormSchema };
